import { contractApi } from '../contractApi';
import { CarveOutView, CarveOutUploadResponse } from '../types/contractTypes';
import { CarveOutGetRequest } from '../requests/carveOutGetRequest';
import { CarveOutsByContractIdRequest } from '../requests/carveOutsByContractIdRequest';
import { CarveOutUpsertRequest } from '../requests/carveOutUpsertRequest';

const carveOutEndpoints = contractApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get CarveOut by ID
    getCarveOut: builder.query<CarveOutView, CarveOutGetRequest>({
      query: ({ id }) => ({
        url: 'CarveOutGetById',
        params: { id },
      }),
      providesTags: (result, error, { id }) => [{ type: 'CarveOut', id }, 'CarveOut'],
    }),

    // Get all CarveOuts by contract ID
    getCarveOutsByContractId: builder.query<CarveOutView[], CarveOutsByContractIdRequest>({
      query: ({ contractId }) => ({
        url: 'CarveOuts',
        method: 'GET',
        params: { contractId },
      }),
      providesTags: (result, error, { contractId }) => [
        { type: 'CarveOutList', id: contractId },
        'CarveOut',
      ],
    }),

    // Upsert CarveOut (create or update)
    upsertCarveOut: builder.mutation<CarveOutView, CarveOutUpsertRequest>({
      query: (body) => ({
        url: 'CarveOut',
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, body) => [
        ...(body.carveOut.id ? [{ type: 'CarveOut' as const, id: body.carveOut.id }] : []),
        { type: 'CarveOutList', id: body.carveOut.contractId },
        'CarveOut',
        'CarveOutList',
      ],
    }),

    // Delete CarveOut
    deleteCarveOut: builder.mutation<CarveOutView, { carveOutId: string; contractId: string }>({
      query: ({ carveOutId }) => ({
        url: 'CarveOutDelete',
        method: 'POST',
        body: { carveOutId },
      }),
      invalidatesTags: (result, error, { contractId }) => [
        'CarveOut',
        { type: 'CarveOutList', id: contractId },
      ],
    }),

    // Upload CarveOuts via CSV file
    uploadCarveOuts: builder.mutation<CarveOutUploadResponse, { contractId: string; file: File }>({
      query: ({ contractId, file }) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          url: `Contract/${contractId}/CarveOuts/Upload`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { contractId }) => [
        { type: 'CarveOutList', id: contractId },
        { type: 'Contract', id: contractId },
        'CarveOut',
        'CarveOutList',
        'Contract',
      ],
    }),

    // Download CarveOuts as CSV file
    downloadCarveOuts: builder.mutation<Blob, { contractId: string }>({
      query: ({ contractId }) => ({
        url: `Contract/${contractId}/CarveOuts/Download`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCarveOutQuery,
  useGetCarveOutsByContractIdQuery,
  useUpsertCarveOutMutation,
  useDeleteCarveOutMutation,
  useUploadCarveOutsMutation,
  useDownloadCarveOutsMutation,
} = carveOutEndpoints;
